import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Space, message, Modal, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Edit, Trash2 } from "lucide-react";
import ModalWrapper from "../../../../Modal";
import AddPublicDay from "../../../../modals/addPublicHoliday";
import { getRequest, deleteRequest, putRequest, postRequest } from '../../../../service';
import { getEmployeeId, getEmployeeData } from "../../../../utils/localStorage";

const AddPublicHoliday = () => {
    const [form] = Form.useForm();
    const [holidays, setHolidays] = useState([]);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedHolidayForStatus, setSelectedHolidayForStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState(null);
    const userID = getEmployeeId();
    const userRole = getEmployeeData()?.role;

    // Permission checks
    const publicHolidayPermissions = getEmployeeData()?.designation?.permissions?.Leaves?.['Public Holiday'] || [];
    
    const canView = userRole === "companyadmin" || publicHolidayPermissions.includes("view");
    const canAdd = userRole === "companyadmin" || publicHolidayPermissions.includes("add");
    const canEdit = userRole === "companyadmin" || publicHolidayPermissions.includes("edit");
    const canDelete = userRole === "companyadmin" || publicHolidayPermissions.includes("delete");

    // Fetch holidays from API
    const fetchHolidays = async () => {
        setLoading(true);
        try {
            if (!canView) {
                setHolidays([]);
                return;
            }
            const res = await getRequest('/api/publicholidays');
            if (res && res.holidays) {
                setHolidays(res.holidays.map(h => ({
                    ...h,
                    id: h._id, // for rowKey
                    details: h.description, // for table
                })));
            } else {
                setHolidays([]);
            }
        } catch (err) {
            message.error('Failed to fetch holidays');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHolidays();
    }, [canView]);

    // Add
    const openAddModal = () => {
        if (!canAdd) {
            message.warning("You don't have permission to add public holidays.");
            return;
        }
        setSelectedHoliday(null);
        setAddModalOpen(true);
    };
    const closeAddModal = () => setAddModalOpen(false);

    // Edit
    const openEditModal = (holiday) => {
        if (!canEdit) {
            message.warning("You don't have permission to edit public holidays.");
            return;
        }
        setSelectedHoliday(holiday);
        setEditModalOpen(true);
    };
    const closeEditModal = () => {
        setSelectedHoliday(null);
        setEditModalOpen(false);
    };

    // Delete
    const [deleteLoading, setDeleteLoading] = useState(false);

    const handleDelete = async (id) => {
        if (!canDelete) {
            message.warning("You don't have permission to delete public holidays.");
            return;
        }
        setDeleteLoading(true);
        try {
            await deleteRequest(`/api/publicholidays?holidayId=${id}`);
            message.success('Holiday deleted successfully');
            fetchHolidays();
        } catch (err) {
            message.error('Failed to delete');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        await handleDelete(deleteId);
        setDeleteModalOpen(false);
        setDeleteId(null);
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setDeleteId(null);
    };

    // Status change handlers
    const openStatusModal = (holiday) => {
        setSelectedHolidayForStatus(holiday);
        setStatusModalOpen(true);
        // Fetch employee data for attendance generation
        fetchEmployeeData();
    };

    // Fetch employee data for attendance generation
    const fetchEmployeeData = async () => {
        try {
            const response = await getRequest('/api/employees');
            if (response?.employees) {
                setEmployeeData(response.employees);
            }
        } catch (error) {
            console.error('Error fetching employee data:', error);
        }
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
        setSelectedHolidayForStatus(null);
    };

    const handleStatusChange = async (status) => {
        const companyData = getEmployeeData();

        if (!selectedHolidayForStatus) {
            message.error('No holiday selected');
            return;
        }
        const receiverIds = Array.isArray(selectedHolidayForStatus.employeeIds)
            ? selectedHolidayForStatus.employeeIds
            : [];
        setStatusLoading(true);
        try {
            // Update holiday status
            await putRequest(`/api/publicholidays?holidayId=${selectedHolidayForStatus.id}`, {
                status: status
            });

            try {
                await postRequest('/api/notifications', {
                    title: `${selectedHolidayForStatus.title}  ${status} (${selectedHolidayForStatus.days} days)`,
                    senderId: companyData.companyId,
                    receiverIds: receiverIds,
                    type: 'PublicHoliday',
                    relatedTypeId: selectedHolidayForStatus.id,
                    relatedDocumentModel: 'PublicHoliday',
                    companyId: companyData?.companyId,
                    priority: 'medium'
                });
            } catch (notifyErr) {
                console.error('Failed to send notifications:', notifyErr);
            }

            // If status is approved, generate and send attendance data
            if (status === 'approved' && employeeData && selectedHolidayForStatus.employeeIds) {
                const generateAttendanceData = () => {
                    const attendanceData = [];
                    const startDate = selectedHolidayForStatus.startDate ? selectedHolidayForStatus.startDate.slice(0, 10) : null;
                    const endDate = selectedHolidayForStatus.endDate ? selectedHolidayForStatus.endDate.slice(0, 10) : null;
                    
                    if (startDate && endDate && selectedHolidayForStatus.employeeIds.length > 0) {
                        // For each selected employee
                        selectedHolidayForStatus.employeeIds.forEach(employeeId => {
                            const employee = employeeData.find(emp => emp.id === employeeId);
                            if (employee) {
                                if (startDate === endDate) {
                                    // Single day holiday
                                    attendanceData.push({
                                        uid: employee.machineCode,
                                        date: startDate,
                                        employeeId: employee.id,
                                        companyId: userID,
                                        checkIn: employee.shift?.startTime || "-",
                                        checkOut: employee.shift?.endTime || "-",
                                        overtime: "-",
                                        reqHour: employee.shift?.workingHours || 9,
                                        workingHour: employee.shift?.workingHours || 9,
                                        status: "Holiday",
                                        approvedbyId: userID,
                                        perDaySelery:  0,
                                    });
                                } else {
                                    // Multiple days holiday - generate data for each day
                                    const start = new Date(startDate);
                                    const end = new Date(endDate);
                                    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                                    
                                    for (let i = 0; i < daysDiff; i++) {
                                        const currentDate = new Date(start);
                                        currentDate.setDate(start.getDate() + i);
                                        const formattedDate = currentDate.toISOString().split('T')[0];
                                        
                                        attendanceData.push({
                                            uid: employee.machineCode || "",
                                            date: formattedDate,
                                            employeeId: employee.id,
                                            companyId: userID,
                                            checkIn: employee.shift?.startTime || "-",
                                            checkOut: employee.shift?.endTime || "-",
                                            overtime: "-",
                                            reqHour: employee.shift?.workingHours || 9,
                                            workingHour: employee.shift?.workingHours || 9,
                                            status: "Holiday",
                                            approvedbyId: userID,
                                            perDaySelery:  0,
                                        });
                                    }
                                }
                            }
                        });
                    }
                    return attendanceData;
                };

                const attendanceData = generateAttendanceData();

                if (attendanceData.length > 0) {
                    try {
                        await postRequest('/api/myattendance', attendanceData);
                        console.log('Attendance data sent successfully:', attendanceData.length, 'records');
                    } catch (error) {
                        console.error('Error sending attendance data:', error);
                        message.warning('Holiday approved but attendance data could not be sent');
                    }
                }
            }

            message.success(`Holiday ${status} successfully`);
            closeStatusModal();
            fetchHolidays(); // Refresh the table
        } catch (error) {
            message.error('Failed to update status');
            console.error('Error updating status:', error);
        } finally {
            setStatusLoading(false);
        }
    };

    // After add/edit, refresh table
    const handleModalSuccess = () => {
        closeAddModal();
        closeEditModal();
        fetchHolidays();
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (date) => date ? date.slice(0, 10) : '',
        },
        {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (date) => date ? date.slice(0, 10) : '',
        },
        {
            title: 'No. of Days',
            dataIndex: 'days',
            key: 'days',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => {
                const statusConfig = {
                    pending: { color: 'orange', text: 'Pending' },
                    approved: { color: 'green', text: 'Approved' },
                    rejected: { color: 'red', text: 'Rejected' }
                };
                const config = statusConfig[status] || { color: 'default', text: status || 'Pending' };
                const canChangeStatus = userRole === 'companyadmin' && status !== 'approved';
                
                const handleStatusClick = () => {
                    if (userRole !== 'companyadmin') {
                        message.warning('Company admin approve public holidays');
                        return;
                    }
                    if (canChangeStatus) {
                        openStatusModal(record);
                    }
                };

                return (
                    <Button
                        type="text"
                        size="small"
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                            status === 'pending' ? '!bg-yellow-500 text-white' :
                            status === 'approved' ? '!bg-green-500 text-white' :
                            status === 'rejected' ? '!bg-red-500 text-white' :
                            'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        } transition-colors`}
                        onClick={handleStatusClick}
                        disabled={status === 'approved'}
                        title={userRole !== 'companyadmin' ? "Company admin approve public holidays" : 
                               status === 'approved' ? "Status cannot be changed" : "Click to change status"}
                    >
                        {config.text}
                    </Button>
                );
            },
        },
        {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const isApproved = record.status === 'approved';
                const canEditRecord = canEdit && !isApproved;
                const canDeleteRecord = canDelete && !isApproved;
                
                return (
                    <Space>
                        <Button 
                            type="text" 
                            icon={<Edit className="w-4 h-4" />} 
                            onClick={() => openEditModal(record)}
                            className={canEditRecord ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
                            disabled={!canEditRecord}
                            title={!canEdit ? "You don't have permission to edit" : isApproved ? "Cannot edit approved holiday" : "Edit holiday"}
                        />
                        <Button
                            type="text"
                            icon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                                setDeleteId(record.id);
                                setDeleteModalOpen(true);
                            }}
                            className={canDeleteRecord ? "text-red-600 hover:text-red-800" : "text-gray-400 cursor-not-allowed"}
                            disabled={!canDeleteRecord}
                            title={!canDelete ? "You don't have permission to delete" : isApproved ? "Cannot delete approved holiday" : "Delete holiday"}
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-semibold">Public Holidays</h1>
                <Button
                    className='bg-primary text-white'
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openAddModal}
                    disabled={!canAdd}
                >
                    Add New Holiday
                </Button>
            </div>
            
            <Table 
                columns={columns} 
                dataSource={canView ? holidays : []}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                loading={loading}
                pagination={{
                    total: canView ? holidays.length : 0,
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />
            {!canView && !loading && (
                <div className="text-center text-gray-500 py-4">You don't have permission to view public holidays.</div>
            )}
            {/* Add Modal */}
            <ModalWrapper
                title="Add New Holiday"
                onRequestClose={closeAddModal}
                open={addModalOpen}
                disabled={!canAdd}
            >
                <AddPublicDay onClose={closeAddModal} onSuccess={handleModalSuccess} />
            </ModalWrapper>
            {/* Edit Modal */}
            <ModalWrapper
                title="Edit Holiday"
                onRequestClose={closeEditModal}
                open={editModalOpen}
                disabled={!canEdit}
            >
                <AddPublicDay onClose={closeEditModal} onSuccess={handleModalSuccess} editData={selectedHoliday} />
            </ModalWrapper>
            {/* Delete Confirmation Modal */}
            <Modal
                title="Confirm Delete"
                open={deleteModalOpen}
                onOk={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmLoading={deleteLoading}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                disabled={!canDelete}
            >
                <p>Are you sure you want to delete this holiday?</p>
                <p>This action cannot be undone.</p>
            </Modal>

            {/* Status Change Modal */}
            <Modal
                title="Holiday Details"
                open={statusModalOpen}
                onCancel={closeStatusModal}
                footer={null}
                width={500}
            >
                {selectedHolidayForStatus && (
                    <div className="space-y-6">
                        {/* Holiday Details */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Title:</span>
                                <span className="font-medium">{selectedHolidayForStatus.title}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">No. of Days:</span>
                                <span className="font-medium">{selectedHolidayForStatus.days}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Start Date:</span>
                                <span className="font-medium">{selectedHolidayForStatus.startDate ? selectedHolidayForStatus.startDate.slice(0, 10) : ''}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">End Date:</span>
                                <span className="font-medium">{selectedHolidayForStatus.endDate ? selectedHolidayForStatus.endDate.slice(0, 10) : ''}</span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-4 pt-4 border-t">
                            <Button
                                type="primary"
                                danger
                                onClick={() => handleStatusChange('rejected')}
                                loading={statusLoading}
                                className="px-8 bg-primary text-white"
                            >
                                Reject
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => handleStatusChange('approved')}
                                loading={statusLoading}
                                className="px-8 bg-primary text-white"
                            >
                                Approve
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}   

export default AddPublicHoliday;
